import { createSelector } from 'reselect'

import { pages } from '~/app/routes'
import { getFlag } from '~/hooks/useFlag'
import { DepartmentKey, SelectedSpecialties } from '~/store/slices/filterSlice'
import { StoreState, useStore } from '~/store/store'

import { selectScheduledSurgeries, selectUnScheduledSurgeries } from '../'
import { filterBySelectedSpecialties } from './filterBySelectedSpecialties'
import { buildFutureOptimisticScheduledSurgeries } from './scheduledSurgeries'
import {
    selectOptimisticUnScheduledSurgeries,
    selectSchedulingBookingIds,
    selectSchedulingSurgeries,
    WAITING_LIST_SCHEDULED_SURGERY_INCLUDE,
    WAITING_LIST_SURGERY_INCLUDE,
    type WaitingListScheduledSurgery,
    type WaitingListSchedulingSurgery,
    type WaitingListSurgery,
} from './surgeries'

const WAITING_LIST_SECTION_INCLUDE = { department: true } as const

const WAITING_LIST_UNCHEDULED_QUERY = {
    include: {
        ...WAITING_LIST_SURGERY_INCLUDE,
        section: WAITING_LIST_SECTION_INCLUDE,
    },
} as const

const WAITING_LIST_SCHEDULED_QUERY = {
    include: {
        ...WAITING_LIST_SCHEDULED_SURGERY_INCLUDE,
        section: WAITING_LIST_SECTION_INCLUDE,
    },
} as const

export type WaitingListItem = WaitingListSurgery | WaitingListScheduledSurgery | WaitingListSchedulingSurgery

type FutureOptimisticScheduledSurgeryForWaitingList = WaitingListScheduledSurgery | WaitingListSchedulingSurgery

export function filterWaitingListItemsByDepartmentKey<T extends WaitingListItem>(surgeries: readonly T[], departmentKey: DepartmentKey): readonly T[] {
    if (departmentKey === 'all') {
        return surgeries
    }

    return surgeries.filter(surgery => surgery.section?.department_id === departmentKey)
}

export function filterWaitingListItemsBySelectedSpecialties<T extends WaitingListItem>(
    surgeries: readonly T[],
    selectedSpecialties: SelectedSpecialties
): readonly T[] {
    return filterBySelectedSpecialties(surgeries, selectedSpecialties)
}

export function combineWaitingListItemsByDepartmentKey(
    futureScheduled: readonly FutureOptimisticScheduledSurgeryForWaitingList[],
    unscheduled: readonly WaitingListSurgery[],
    departmentKey: DepartmentKey
): WaitingListItem[] {
    return [...filterWaitingListItemsByDepartmentKey(futureScheduled, departmentKey), ...filterWaitingListItemsByDepartmentKey(unscheduled, departmentKey)]
}

/** Future-dated surgeries either scheduled or optimistically being scheduled. */
export const selectFutureOptimisticScheduledSurgeriesForWaitingList = createSelector(
    (state: StoreState) => selectScheduledSurgeries(state, WAITING_LIST_SCHEDULED_QUERY),
    (state: StoreState) =>
        selectUnScheduledSurgeries(state, {
            ...WAITING_LIST_UNCHEDULED_QUERY,
            where: { bookingId: { in: selectSchedulingBookingIds(state) } },
        }),
    selectSchedulingSurgeries,
    (allScheduled, schedulingUnScheduled, schedulingParams): FutureOptimisticScheduledSurgeryForWaitingList[] =>
        buildFutureOptimisticScheduledSurgeries(allScheduled, schedulingUnScheduled, schedulingParams)
)

/** All waiting-list rows across departments. */
export const selectAllWaitingListItems = createSelector(
    selectFutureOptimisticScheduledSurgeriesForWaitingList,
    selectOptimisticUnScheduledSurgeries,
    (futureScheduled, unscheduled): WaitingListItem[] => combineWaitingListItemsByDepartmentKey(futureScheduled, unscheduled, 'all')
)

// used for prediction metrics, cannot be migrated to specialties yet, as those are dependent on departments
export function useAllWaitingListItems(): WaitingListItem[] {
    return useStore(selectAllWaitingListItems)
}

/** Waiting-list rows for the user's currently selected department filter. */
export const selectWaitingListItemsBySelectedDepartmentOrSpecialties = createSelector(
    selectFutureOptimisticScheduledSurgeriesForWaitingList,
    selectOptimisticUnScheduledSurgeries,
    (state: StoreState) => state.appFilters.departmentKey,
    (state: StoreState) => state.appFilters.selectedSpecialties,
    (futureScheduled, unscheduled, departmentKey, selectedSpecialties): WaitingListItem[] => {
        if (getFlag('use_specialties_selection')) {
            return [
                ...filterWaitingListItemsBySelectedSpecialties(futureScheduled, selectedSpecialties),
                ...filterWaitingListItemsBySelectedSpecialties(unscheduled, selectedSpecialties),
            ]
        }

        return combineWaitingListItemsByDepartmentKey(futureScheduled, unscheduled, departmentKey)
    }
)

export function useWaitingListItemsBySelectedDepartmentOrSpecialties(): WaitingListItem[] {
    return useStore(selectWaitingListItemsBySelectedDepartmentOrSpecialties)
}

/** Waiting-list rows for the current department/specialty scope and active tab view. */
export const selectWaitingListItemsForActiveView = createSelector(
    [
        selectFutureOptimisticScheduledSurgeriesForWaitingList,
        selectOptimisticUnScheduledSurgeries,
        (state: StoreState) => state.appFilters.departmentKey,
        (state: StoreState) => state.appFilters.selectedSpecialties,
        (state: StoreState) => state.app.activeViews.WAITING_LIST,
    ],
    (futureOptimisticScheduled, unscheduled, departmentKey, selectedSpecialties, activeView): readonly WaitingListItem[] => {
        const isSpecialtiesSelectionEnabled = getFlag('use_specialties_selection')
        const futureScheduledByScope = isSpecialtiesSelectionEnabled
            ? filterWaitingListItemsBySelectedSpecialties(futureOptimisticScheduled, selectedSpecialties)
            : filterWaitingListItemsByDepartmentKey(futureOptimisticScheduled, departmentKey)
        const unscheduledByScope = isSpecialtiesSelectionEnabled
            ? filterWaitingListItemsBySelectedSpecialties(unscheduled, selectedSpecialties)
            : filterWaitingListItemsByDepartmentKey(unscheduled, departmentKey)

        if (activeView === pages.WAITING_LIST.views.scheduled.path) {
            return futureScheduledByScope
        }

        if (activeView === pages.WAITING_LIST.views.unscheduled.path) {
            return unscheduledByScope
        }

        return [...futureScheduledByScope, ...unscheduledByScope]
    }
)

/** Waiting-list rows for the current department/specialty scope and active tab view. */
export function useWaitingListItemsForActiveView(): readonly WaitingListItem[] {
    return useStore(selectWaitingListItemsForActiveView)
}
